import { API_ENDPOINTS } from '@/api/endpoints';
import { apiFetch } from '@/util/apiClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ApiSubprojectBreakdown {
  id?: number;
  subproject: string;
  ownerAmountFiscalyear: string | null;
  contractorAmountFiscalyear: string | null;
  ownerAmountTotal: string | null;
  contractorAmountTotal: string | null;
}

export interface ApiLineItem {
  id?: number;
  section: string;
  category: string;
  label: string;
  ownerAmountFiscalyear: string | null;
  contractorAmountFiscalyear: string | null;
  ownerAmountTotal: string | null;
  contractorAmountTotal: string | null;
  sortOrder: number;
  subprojectBreakdowns: ApiSubprojectBreakdown[];
}

export interface ApiCostEstimate {
  id: number;
  project: string;
  dataSource: string | null;
  referenceYear: number | null;
  rateInflationOwner: string | null;
  rateInflationLand: string | null;
  rateInflationCostEscalation: string | null;
  /** The USER's local cost multiplier (editable). */
  localCostMultiplier: string | null;
  /**
   * The SYSTEM's regional multiplier, derived from the project's shapes.
   * Read-only (ignored on PUT). null when the project's subprojects resolve
   * to different multipliers, or it has none.
   */
  regionalMultiplier: string | null;
  /** 'local' | 'national' | null (null = not stated) */
  costBasis: string | null;
  lineItems: ApiLineItem[];
}

export interface CostEstimateUpdatePayload {
  dataSource: string | null;
  referenceYear: number | null;
  rateInflationOwner: string | null;
  rateInflationLand: string | null;
  rateInflationCostEscalation: string | null;
  localCostMultiplier: string | null;
  /** 'local' | 'national' | null (null = not stated) */
  costBasis: string | null;
  lineItems: {
    section: string;
    category: string;
    label: string;
    ownerAmountFiscalyear: string | null;
    contractorAmountFiscalyear: string | null;
    ownerAmountTotal: string | null;
    contractorAmountTotal: string | null;
    sortOrder: number;
    subprojectBreakdowns: {
      subproject: string;
      ownerAmountFiscalyear: string | null;
      contractorAmountFiscalyear: string | null;
      ownerAmountTotal: string | null;
      contractorAmountTotal: string | null;
    }[];
  }[];
}

export function useGetCostEstimate(
  orgId: string | undefined,
  projectId: string | undefined,
  options?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: ['cost-estimate', orgId, projectId],
    queryFn: () =>
      apiFetch<ApiCostEstimate>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.COST_ESTIMATE(orgId!, projectId!),
      ),
    enabled: !!orgId && !!projectId,
    ...options,
  });
}

export function useUpdateCostEstimate(
  orgId: string | undefined,
  projectId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CostEstimateUpdatePayload) =>
      apiFetch<ApiCostEstimate>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.COST_ESTIMATE(orgId!, projectId!),
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['cost-estimate', orgId, projectId], data);
    },
  });
}
